import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { personnelService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Save, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};
const fmtPct = (v) => {
  if (v === null || v === undefined || !isFinite(v)) return '-';
  return `${v.toFixed(1)}%`;
};
const n = (v) => (v !== null && v !== undefined && v !== '' ? parseFloat(v) : null);
const safeAdd = (...args) => {
  const nums = args.map(n).filter(v => v !== null);
  return nums.length > 0 ? nums.reduce((a, b) => a + b, 0) : null;
};

export default function PersonnelKPIPage() {
  const { user } = useAuth();
  const canEdit = ['admin', 'management', 'quality'].includes(user?.role);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await personnelService.getByYear(year);
      setData(res.data);
      setDirty({});
    } catch { toast.error('Errore caricamento dati'); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleChange = (month, field, val) => {
    setData(prev => prev.map(r => r.month === month ? { ...r, [field]: val } : r));
    setDirty(prev => ({ ...prev, [month]: true }));
  };

  const saveAll = async () => {
    const dirtyMonths = Object.keys(dirty).map(Number);
    if (dirtyMonths.length === 0) { toast.info('Nessuna modifica'); return; }
    setSaving(true);
    try {
      const items = dirtyMonths.map(m => {
        const r = data.find(row => row.month === m);
        return {
          year, month: m,
          direct_operators_count: n(r.direct_operators_count) !== null ? parseInt(r.direct_operators_count) : null,
          direct_internal_cost: n(r.direct_internal_cost),
          direct_external_cost: n(r.direct_external_cost),
          indirect_cost: n(r.indirect_cost),
          consultant_cost: n(r.consultant_cost),
          target_percentage: n(r.target_percentage),
          notes: r.notes || null,
        };
      });
      await personnelService.bulkUpsert(items);
      setDirty({});
      toast.success(`${dirtyMonths.length} mesi salvati`);
      load();
    } catch { toast.error('Errore salvataggio'); } finally { setSaving(false); }
  };

  const computed = useMemo(() => {
    return data.map(r => {
      const revenue = n(r.revenue);
      const intCost = n(r.direct_internal_cost);
      const extCost = n(r.direct_external_cost);
      const directTotal = safeAdd(intCost, extCost);
      const indirectCost = n(r.indirect_cost);
      const consultCost = n(r.consultant_cost);
      const totalCost = safeAdd(directTotal, indirectCost, consultCost);
      const pct = (totalCost !== null && revenue !== null && revenue !== 0) ? (totalCost / revenue) * 100 : null;
      const target = n(r.target_percentage);
      const diff = (pct !== null && target !== null) ? pct - target : null;
      const directPct = (directTotal !== null && revenue && revenue !== 0) ? (directTotal / revenue) * 100 : null;
      const indirectPct = (indirectCost !== null && revenue && revenue !== 0) ? (indirectCost / revenue) * 100 : null;
      const consultPct = (consultCost !== null && revenue && revenue !== 0) ? (consultCost / revenue) * 100 : null;
      return { ...r, revenue, directTotal, totalCost, pct, target, diff, directPct, indirectPct, consultPct };
    });
  }, [data]);

  const summary = useMemo(() => {
    let totalCost = 0, totalRevenue = 0, pctSum = 0, pctCount = 0, currentPct = null, diffSum = 0, diffCount = 0;
    const now = new Date().getMonth() + 1;
    computed.forEach(r => {
      if (r.totalCost !== null) totalCost += r.totalCost;
      if (r.revenue !== null) totalRevenue += r.revenue;
      if (r.pct !== null) { pctSum += r.pct; pctCount++; }
      if (r.diff !== null) { diffSum += r.diff; diffCount++; }
      if (r.month === now && r.pct !== null) currentPct = r.pct;
    });
    const avgPct = pctCount > 0 ? pctSum / pctCount : null;
    const avgDiff = diffCount > 0 ? diffSum / diffCount : null;
    return { totalCost, totalRevenue, avgPct, currentPct, avgDiff };
  }, [computed]);

  const trend = useMemo(() => {
    const recent = computed.filter(r => r.pct !== null).slice(-3);
    if (recent.length < 2) return { label: 'Dati insufficienti', icon: Minus, color: 'text-slate-500' };
    let ups = 0, downs = 0;
    for (let i = 1; i < recent.length; i++) {
      if (recent[i].pct > recent[i - 1].pct) ups++;
      else if (recent[i].pct < recent[i - 1].pct) downs++;
    }
    if (ups > downs) return { label: 'Incidenza in aumento', icon: TrendingUp, color: 'text-red-600' };
    if (downs > ups) return { label: 'Incidenza in calo', icon: TrendingDown, color: 'text-emerald-600' };
    return { label: 'Incidenza stabile', icon: Minus, color: 'text-amber-600' };
  }, [computed]);

  const lineChartData = useMemo(() =>
    computed.map(r => ({ name: MONTHS[r.month - 1], 'Effettiva %': r.pct !== null ? parseFloat(r.pct.toFixed(1)) : null, 'Target %': r.target })),
  [computed]);

  const barChartData = useMemo(() =>
    computed.map(r => ({ name: MONTHS[r.month - 1], Diretti: r.directTotal, Indiretti: n(r.indirect_cost), Consulenti: n(r.consultant_cost) })),
  [computed]);

  const hasDirty = Object.keys(dirty).length > 0;

  return (
    <div className="space-y-5" data-testid="personnel-kpi-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="personnel-title">IND002 – Impatto Personale</h1>
          <p className="text-sm text-slate-500 mt-0.5">Incidenza costo personale su fatturato</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
            <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-prev"><ChevronLeft className="w-4 h-4 text-slate-600" /></button>
            <span className="text-sm font-bold text-slate-900 min-w-[4rem] text-center" data-testid="year-display">{year}</span>
            <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-next"><ChevronRight className="w-4 h-4 text-slate-600" /></button>
          </div>
          {canEdit && hasDirty && (
            <Button size="sm" onClick={saveAll} disabled={saving} data-testid="save-all-btn">
              <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva tutto'}
            </Button>
          )}
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-kpis">
        <KpiCard label="Costo Totale Personale" value={fmt(summary.totalCost)} unit="EUR" testId="kpi-total-cost" />
        <KpiCard label="% Media Annua" value={fmtPct(summary.avgPct)} accent={summary.avgPct !== null && summary.avgPct <= (computed.find(r => r.target !== null)?.target || 100) ? 'text-emerald-600' : 'text-red-600'} testId="kpi-avg-pct" />
        <KpiCard label="% Mese Corrente" value={fmtPct(summary.currentPct)} testId="kpi-current-pct" />
        <KpiCard label="Diff. Media vs Target" value={summary.avgDiff !== null ? (summary.avgDiff >= 0 ? '+' : '') + fmtPct(summary.avgDiff) : '-'} accent={summary.avgDiff !== null ? (summary.avgDiff <= 0 ? 'text-emerald-600' : 'text-red-600') : ''} testId="kpi-diff" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Line Chart - % Effettiva vs Target */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="pct-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Incidenza % Personale {year}</h2>
            <div className={`flex items-center gap-1.5 text-sm font-medium ${trend.color}`} data-testid="trend-indicator">
              <trend.icon className="w-4 h-4" /><span>{trend.label}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={lineChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => `${v}%`} />
              <Tooltip formatter={(v) => [`${v}%`, undefined]} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Legend wrapperStyle={{ fontSize: '13px' }} />
              <Line type="monotone" dataKey="Target %" stroke="#94a3b8" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="Effettiva %" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a' }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Stacked Bar Chart - Cost Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="cost-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Composizione Costi Personale {year}</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barChartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => fmt(v)} />
              <Tooltip formatter={(v) => [fmt(v) + ' EUR', undefined]} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }} />
              <Legend wrapperStyle={{ fontSize: '13px' }} />
              <Bar dataKey="Diretti" stackId="a" fill="#0f172a" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Indiretti" stackId="a" fill="#64748b" />
              <Bar dataKey="Consulenti" stackId="a" fill="#cbd5e1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="personnel-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2.5 w-24">Mese</th>
                  <th className="px-3 py-2.5 text-right w-28">Fatturato</th>
                  <th className="px-3 py-2.5 text-right w-24">Operatori</th>
                  <th className="px-3 py-2.5 text-right w-28">C. Interno</th>
                  <th className="px-3 py-2.5 text-right w-28">C. Esterno</th>
                  <th className="px-3 py-2.5 text-right w-28">Indiretto</th>
                  <th className="px-3 py-2.5 text-right w-28">Consulenti</th>
                  <th className="px-3 py-2.5 text-right w-28">Totale</th>
                  <th className="px-3 py-2.5 text-right w-16">%</th>
                  <th className="px-3 py-2.5 text-right w-16">Target</th>
                  <th className="px-3 py-2.5 text-right w-16">Diff.</th>
                  <th className="px-3 py-2.5 w-32">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {computed.map(r => {
                  const isDirty = dirty[r.month];
                  return (
                    <tr key={r.month} className={`transition-colors ${isDirty ? 'bg-amber-50' : 'hover:bg-slate-50'}`} data-testid={`row-${r.month}`}>
                      <td className="px-3 py-1.5 font-medium text-slate-700 text-sm">{MONTHS[r.month - 1]}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-slate-500 text-xs" data-testid={`revenue-${r.month}`}>{fmt(r.revenue)}</td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.direct_operators_count} onChange={v => handleChange(r.month, 'direct_operators_count', v)} canEdit={canEdit} testId={`operators-${r.month}`} step="1" /></td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.direct_internal_cost} onChange={v => handleChange(r.month, 'direct_internal_cost', v)} canEdit={canEdit} testId={`int-cost-${r.month}`} /></td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.direct_external_cost} onChange={v => handleChange(r.month, 'direct_external_cost', v)} canEdit={canEdit} testId={`ext-cost-${r.month}`} /></td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.indirect_cost} onChange={v => handleChange(r.month, 'indirect_cost', v)} canEdit={canEdit} testId={`indirect-${r.month}`} /></td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.consultant_cost} onChange={v => handleChange(r.month, 'consultant_cost', v)} canEdit={canEdit} testId={`consult-${r.month}`} /></td>
                      <td className="px-3 py-1.5 text-right font-mono text-sm font-semibold text-slate-900" data-testid={`total-${r.month}`}>{fmt(r.totalCost)}</td>
                      <td className="px-3 py-1.5 text-right">
                        <span className={`font-mono text-sm font-semibold ${r.pct !== null ? (r.diff !== null && r.diff <= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`} data-testid={`pct-${r.month}`}>{fmtPct(r.pct)}</span>
                      </td>
                      <td className="px-3 py-1.5"><CellInput type="number" value={r.target_percentage} onChange={v => handleChange(r.month, 'target_percentage', v)} canEdit={canEdit} testId={`target-${r.month}`} step="0.1" /></td>
                      <td className="px-3 py-1.5 text-right">
                        <span className={`font-mono text-xs ${r.diff !== null ? (r.diff <= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`} data-testid={`diff-${r.month}`}>
                          {r.diff !== null ? (r.diff >= 0 ? '+' : '') + r.diff.toFixed(1) + '%' : '-'}
                        </span>
                      </td>
                      <td className="px-3 py-1.5"><CellInput type="text" value={r.notes} onChange={v => handleChange(r.month, 'notes', v)} canEdit={canEdit} testId={`notes-${r.month}`} placeholder="..." /></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-3 py-2.5 text-slate-900">Totale</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-700" data-testid="foot-revenue">{fmt(summary.totalRevenue)}</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-900" data-testid="foot-total">{fmt(summary.totalCost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono" data-testid="foot-avg-pct">{fmtPct(summary.avgPct)}</td>
                  <td className="px-3 py-2.5"></td>
                  <td className="px-3 py-2.5 text-right font-mono" data-testid="foot-diff">
                    <span className={summary.avgDiff !== null ? (summary.avgDiff <= 0 ? 'text-emerald-600' : 'text-red-600') : ''}>
                      {summary.avgDiff !== null ? (summary.avgDiff >= 0 ? '+' : '') + summary.avgDiff.toFixed(1) + '%' : '-'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CellInput({ type, value, onChange, canEdit, testId, step, placeholder }) {
  if (!canEdit) return <span className="font-mono text-right block text-sm">{value !== null && value !== undefined ? value : '-'}</span>;
  return (
    <input
      type={type}
      step={step || 'any'}
      value={value !== null && value !== undefined ? value : ''}
      onChange={e => onChange(e.target.value)}
      className="w-full px-1.5 py-0.5 text-xs font-mono text-right border border-transparent hover:border-slate-200 focus:border-slate-400 rounded focus:outline-none focus:ring-1 focus:ring-slate-400 bg-transparent"
      placeholder={placeholder || '-'}
      data-testid={testId}
    />
  );
}

function KpiCard({ label, value, unit, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
      {unit && <p className="text-xs text-slate-400 mt-0.5">{unit}</p>}
    </div>
  );
}
